import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  ViewModule,
  ViewList,
} from '@mui/icons-material';
import { getSquads, Squad, archiveSquad, unarchiveSquad } from '../api/squad.api';
import { squadMemberApi } from '../api/squad-member.api';
import { SquadFormDialog } from '../components/squads/SquadFormDialog';

export const SquadsPage = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [skillLevel, setSkillLevel] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSquad, setEditingSquad] = useState<Squad | null>(null);
  const [success, setSuccess] = useState('');
  const [registrationStatuses, setRegistrationStatuses] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'capacity' | 'skillLevel' | 'ageGroup'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const canManageSquads = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH';
  const isPlayer = user?.role === 'PLAYER';
  const isParent = user?.role === 'PARENT';

  const getAgeGroupColor = (ageGroup?: string) => {
    switch (ageGroup?.toLowerCase()) {
      case 'red':
        return '#e53935';
      case 'orange':
        return '#fb8c00';
      case 'green':
        return '#43a047';
      case 'yellow':
        return '#fdd835';
      case 'adult':
        return '#fdd835';
      default:
        return '#1a1a1a';
    }
  };

  const fetchSquads = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getSquads({
        search,
        skillLevel: skillLevel || undefined,
        ageGroup: ageGroup || undefined,
        status: showArchived ? 'archived' : undefined,
      });
      setSquads(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load squads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSquads();
  }, [search, skillLevel, ageGroup, showArchived]);

  useEffect(() => {
    if (isPlayer && user?.id && squads.length > 0) {
      fetchRegistrationStatuses();
    }
  }, [squads, isPlayer, user?.id]);

  const fetchRegistrationStatuses = async () => {
    if (!user?.id) return;

    try {
      const statuses: Record<string, string> = {};

      // Fetch memberships for all squads at once
      for (const squad of squads) {
        try {
          const response = await squadMemberApi.getSquadMembers(squad.id, false);
          const members = response.data.data?.members || [];
          const myMembership = members.find((m: any) => m.player?.id === user.id);

          if (myMembership) {
            statuses[squad.id] = myMembership.status;
          }
        } catch (err) {
          // Ignore errors for individual squads
          console.error(`Failed to fetch status for squad ${squad.id}`, err);
        }
      }

      setRegistrationStatuses(statuses);
    } catch (err) {
      console.error('Failed to fetch registration statuses', err);
    }
  };

  const handleCreateSquad = () => {
    setEditingSquad(null);
    setDialogOpen(true);
  };

  const handleEditSquad = (squad: Squad) => {
    setEditingSquad(squad);
    setDialogOpen(true);
  };

  const handleArchiveSquad = async (squadId: string) => {
    try {
      await archiveSquad(squadId);
      fetchSquads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to archive squad');
    }
  };

  const handleUnarchiveSquad = async (squadId: string) => {
    try {
      await unarchiveSquad(squadId);
      fetchSquads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unarchive squad');
    }
  };

  const handleDialogClose = (refreshNeeded?: boolean) => {
    setDialogOpen(false);
    setEditingSquad(null);
    if (refreshNeeded) {
      fetchSquads();
    }
  };

  const handleViewSquad = (squadId: string) => {
    navigate(`/squads/${squadId}`);
  };

  const getCapacityColor = (current: number, max: number) => {
    const percentage = (current / max) * 100;
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'success';
  };

  const getSkillLevelColor = (level?: string) => {
    switch (level) {
      case 'beginner': return 'info';
      case 'intermediate': return 'primary';
      case 'advanced': return 'secondary';
      case 'elite': return 'error';
      default: return 'default';
    }
  };

  const getRegistrationStatusChip = (squadId: string) => {
    const status = registrationStatuses[squadId];

    if (!status) {
      return null;
    }

    switch (status) {
      case 'active':
        return <Chip label="Active" color="success" size="small" />;
      case 'pending':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'waitlist':
        return <Chip label="Waitlist" color="info" size="small" />;
      case 'withdrawn':
        return <Chip label="Withdrawn" color="default" size="small" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" />;
      default:
        return null;
    }
  };

  const getSortedSquads = () => {
    return [...squads].sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'name':
          compareValue = a.name.localeCompare(b.name);
          break;
        case 'capacity':
          compareValue = (a.currentCapacity / a.maxCapacity) - (b.currentCapacity / b.maxCapacity);
          break;
        case 'skillLevel':
          const skillLevelOrder = { beginner: 1, intermediate: 2, advanced: 3, elite: 4 };
          compareValue = (skillLevelOrder[a.skillLevel as keyof typeof skillLevelOrder] || 0) -
                        (skillLevelOrder[b.skillLevel as keyof typeof skillLevelOrder] || 0);
          break;
        case 'ageGroup':
          compareValue = (a.ageGroup || '').localeCompare(b.ageGroup || '');
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
  };

  const sortedSquads = getSortedSquads();

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          GKM Squads
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModule />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewList />
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="capacity">Capacity</MenuItem>
              <MenuItem value="skillLevel">Skill Level</MenuItem>
              <MenuItem value="ageGroup">Age Group</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={sortOrder}
            exclusive
            onChange={(_, newOrder) => newOrder && setSortOrder(newOrder)}
            size="small"
          >
            <ToggleButton value="asc" aria-label="ascending">
              ↑
            </ToggleButton>
            <ToggleButton value="desc" aria-label="descending">
              ↓
            </ToggleButton>
          </ToggleButtonGroup>

          {canManageSquads && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSquad}
              sx={{ borderRadius: 2 }}
            >
              Create Squad
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search squads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Skill Level"
              value={skillLevel}
              onChange={(e) => setSkillLevel(e.target.value)}
            >
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
              <MenuItem value="elite">Elite</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Age Group"
              value={ageGroup}
              onChange={(e) => setAgeGroup(e.target.value)}
            >
              <MenuItem value="">All Ages</MenuItem>
              <MenuItem value="red">Red</MenuItem>
              <MenuItem value="orange">Orange</MenuItem>
              <MenuItem value="green">Green</MenuItem>
              <MenuItem value="yellow">Yellow</MenuItem>
              <MenuItem value="adult">Adult</MenuItem>
            </TextField>
          </Grid>
          {canManageSquads && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArchiveIcon fontSize="small" />
                    <Typography>Show Archived Squads</Typography>
                  </Box>
                }
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {loading ? (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      ) : squads.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No squads found
          </Typography>
          {canManageSquads && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSquad}
              sx={{ mt: 2 }}
            >
              Create Your First Squad
            </Button>
          )}
        </Box>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {sortedSquads.map((squad) => (
            <Grid item xs={12} sm={6} md={4} key={squad.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                  borderTop: `4px solid ${getAgeGroupColor(squad.ageGroup)}`,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {squad.name}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {squad.status === 'archived' && (
                      <Chip
                        label="ARCHIVED"
                        size="small"
                        sx={{
                          bgcolor: '#757575',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {squad.skillLevel && (
                      <Chip
                        label={squad.skillLevel.toUpperCase()}
                        size="small"
                        color={getSkillLevelColor(squad.skillLevel)}
                      />
                    )}
                    {squad.ageGroup && (
                      <Chip
                        label={squad.ageGroup.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: getAgeGroupColor(squad.ageGroup),
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {squad.description || 'No description available'}
                  </Typography>

                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        <PeopleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                        Capacity
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {squad.currentCapacity} / {squad.maxCapacity}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(squad.currentCapacity / squad.maxCapacity) * 100}
                      color={getCapacityColor(squad.currentCapacity, squad.maxCapacity)}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Coach: {squad.coach ? `${squad.coach.firstName} ${squad.coach.lastName}` : 'Not assigned'}
                  </Typography>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewSquad(squad.id)}
                  >
                    View Details
                  </Button>
                  {(isPlayer || isParent) && squad.status !== 'archived' && (
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                      {getRegistrationStatusChip(squad.id)}
                    </Box>
                  )}
                  {canManageSquads && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleEditSquad(squad)}
                        sx={{ ml: (isPlayer || isParent) ? 0 : 'auto' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {squad.status === 'archived' ? (
                        <Tooltip title="Unarchive Squad">
                          <IconButton
                            size="small"
                            onClick={() => handleUnarchiveSquad(squad.id)}
                            color="primary"
                          >
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Archive Squad">
                          <IconButton
                            size="small"
                            onClick={() => handleArchiveSquad(squad.id)}
                            color="warning"
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <List>
          {sortedSquads.map((squad) => (
            <Paper key={squad.id} sx={{ mb: 2 }}>
              <ListItem
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  p: 2,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', width: '100%', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {squad.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {squad.status === 'archived' && (
                          <Chip
                            label="ARCHIVED"
                            size="small"
                            sx={{
                              bgcolor: '#757575',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {squad.skillLevel && (
                          <Chip
                            label={squad.skillLevel.toUpperCase()}
                            size="small"
                            color={getSkillLevelColor(squad.skillLevel)}
                          />
                        )}
                        {squad.ageGroup && (
                          <Chip
                            label={squad.ageGroup.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: getAgeGroupColor(squad.ageGroup),
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {(isPlayer || isParent) && squad.status !== 'archived' && getRegistrationStatusChip(squad.id)}
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {squad.description || 'No description available'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ minWidth: 200 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            <PeopleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                            Capacity
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {squad.currentCapacity} / {squad.maxCapacity}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(squad.currentCapacity / squad.maxCapacity) * 100}
                          color={getCapacityColor(squad.currentCapacity, squad.maxCapacity)}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        Coach: {squad.coach ? `${squad.coach.firstName} ${squad.coach.lastName}` : 'Not assigned'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewSquad(squad.id)}
                    >
                      View
                    </Button>
                    {canManageSquads && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleEditSquad(squad)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {squad.status === 'archived' ? (
                          <Tooltip title="Unarchive Squad">
                            <IconButton
                              size="small"
                              onClick={() => handleUnarchiveSquad(squad.id)}
                              color="primary"
                            >
                              <UnarchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Archive Squad">
                            <IconButton
                              size="small"
                              onClick={() => handleArchiveSquad(squad.id)}
                              color="warning"
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {dialogOpen && (
        <SquadFormDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          squad={editingSquad}
        />
      )}
    </Container>
  );
};
